import { useEffect, useId } from "react";

import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useBrebReceivingKey } from "@/shared/hooks/useBrebReceivingKey";
import useTranslations from "@/shared/lang/useTranslations";
import { activeBrebKeys } from "@/shared/services/payments/brebReceivingKey.service";

import { BREB_KEY_SOURCE_NAME } from "./brebKeySources";

export interface BrebReceivingKeySelectProps {
  /** The chosen key; null follows the primary one, which the backend picks by itself. */
  value: string | null;
  onChange: (keyId: string | null) => void;
  disabled?: boolean;
  organizationId?: string;
}

// Renders only when there is a choice to make: two or more ACTIVE keys.
export function BrebReceivingKeySelect({
  value,
  onChange,
  disabled,
  organizationId,
}: BrebReceivingKeySelectProps) {
  const { t } = useTranslations("payins");
  const id = useId();
  const { state } = useBrebReceivingKey(organizationId);
  const active = activeBrebKeys(state);
  const primary = active[0] ?? null;
  const stale = value !== null && !active.some((key) => key.id === value);

  // A key deleted or no longer active since it was picked falls back to the primary instead of failing the payin.
  useEffect(() => {
    if (state && stale) onChange(null);
  }, [state, stale, onChange]);

  if (active.length < 2 || !primary) return null;

  return (
    <div className="space-y-1.5">
      <ColocaLabel htmlFor={id}>{t("breb_key_select_label")}</ColocaLabel>
      <ColocaSelect
        id={id}
        value={stale || value === null ? primary.id : value}
        onValueChange={(next) => onChange(next === primary.id ? null : next)}
        disabled={disabled}
        options={active.map((key) => ({
          value: key.id,
          label: (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate font-mono">{key.key_value}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {key.is_primary
                  ? t("breb_key_select_option_primary", { source: t(BREB_KEY_SOURCE_NAME[key.source]) })
                  : t(BREB_KEY_SOURCE_NAME[key.source])}
              </span>
            </span>
          ),
        }))}
      />
      <p className="text-xs text-muted-foreground">{t("breb_key_select_hint")}</p>
    </div>
  );
}
